import { ClientColumn } from "@common/types/views/clientViews";
import { WorkspaceSortValue } from "@common/types/views/workspaceViews";
import { ViewConfig } from "@lib/views";
import {
    ClientSchema,
    ClientEntitySchema,
} from "@common/schemas/client.schema";
import { NameCell } from "./cells/NameCell";
import { EntitiesCell } from "./cells/EntitiesCell";
import { TeamCell } from "./cells/TeamCell";
import { MembersCell } from "./cells/MembersCell";
import { ActionsCell } from "./cells/ActionCell";
import { PlanCell } from "./cells/PlanCell";
import { StartDateCell } from "./cells/StartDateCell";
import { PriceMonthlyCell } from "./cells/PriceMonthly";
import { BillingOwnerCell } from "./cells/BillingOwnershipCell";
import { ServicesCell } from "./cells/ServicesCell";
import { NoteCell } from "./cells/NoteCell";
import { LegalTypeCell } from "./cells/LegalTypeCell";
import { SubscriptionStartDateCell } from "./cells/SubscriptionStartDateCell";

export interface ClientDetailsRow extends ClientSchema {
    type: "workspace" | "entity";
    iconColor?: ClientEntitySchema["iconColor"];
    logo?: ClientEntitySchema["logo"];
    workspaceId?: ClientEntitySchema["workspaceId"];
    isBusiness?: ClientEntitySchema["isBusiness"];

    closeStatus: ClientEntitySchema["closeStatus"];
    closeDate: ClientEntitySchema["closeDate"];
    dueDate: ClientEntitySchema["dueDate"];
    priority: ClientEntitySchema["priority"];
    legalType: ClientEntitySchema["legalType"];
    closeStage: ClientEntitySchema["closeStage"];
    closeStageUpdatedAt: ClientEntitySchema["closeStageUpdatedAt"];
}

export const clientViewConfig: ViewConfig<
    ClientDetailsRow,
    ClientColumn,
    WorkspaceSortValue
> = {
    columns: {
        [ClientColumn.NAME]: {
            accessorKey: "name",
            name: "Client",
            header: "Client",
            cell: NameCell,
            sort: {
                asc: WorkspaceSortValue.NAME_ASC,
                desc: WorkspaceSortValue.NAME_DESC,
            },
            immutable: true,
            minSize: 300,
            maxSize: 800,
        },
        [ClientColumn.ENTITIES]: {
            accessorKey: "entities",
            name: "Entities",
            header: "Entities",
            cell: EntitiesCell,
            minSize: 160,
            maxSize: 160,
        },
        [ClientColumn.TEAM]: {
            accessorKey: "team",
            name: "Team",
            header: "Team",
            cell: TeamCell,
            minSize: 140,
            maxSize: 180,
        },
        [ClientColumn.MEMBERS]: {
            accessorKey: "members",
            name: "Members",
            header: "Members",
            cell: MembersCell,
            minSize: 150,
            maxSize: 180,
        },
        [ClientColumn.ACTIONS]: {
            accessorKey: "actions",
            name: "Actions",
            header: "",
            cell: ActionsCell,
            minSize: 130,
            maxSize: 130,
            immutable: true,
            hiddenFromSettings: true,
        },
        [ClientColumn.PLAN]: {
            accessorKey: "plan",
            name: "Plan",
            header: "Plan",
            cell: PlanCell,
            sort: {
                asc: WorkspaceSortValue.PLAN_ASC,
                desc: WorkspaceSortValue.PLAN_DESC,
            },
            minSize: 100,
            maxSize: 100,
        },
        [ClientColumn.START_DATE]: {
            accessorKey: "bookkeepingStartDate",
            name: "Start date",
            header: "Start date",
            cell: StartDateCell,
            sort: {
                asc: WorkspaceSortValue.START_DATE_ASC,
                desc: WorkspaceSortValue.START_DATE_DESC,
            },
            minSize: 180,
            maxSize: 180,
        },
        [ClientColumn.PRICE_MONTHLY]: {
            accessorKey: "plan",
            name: "Monthly cost",
            header: "Monthly cost",
            cell: PriceMonthlyCell,
            minSize: 100,
            maxSize: 100,
        },
        [ClientColumn.BILLING_OWNERSHIP]: {
            accessorKey: "organizationAssignments",
            name: "Who pays",
            header: "Who pays",
            cell: BillingOwnerCell,
            minSize: 220,
            maxSize: 220,
        },
        [ClientColumn.SERVICES]: {
            accessorKey: "services",
            name: "Services",
            header: "Services",
            cell: ServicesCell,
            minSize: 200,
            maxSize: 360,
        },
        [ClientColumn.NOTE]: {
            accessorKey: "note",
            name: "Memo",
            header: "Memo",
            cell: NoteCell,
            minSize: 200,
            maxSize: 360,
        },
        [ClientColumn.LEGAL_TYPE]: {
            accessorKey: "legalType",
            name: "Entity type",
            header: "Entity type",
            cell: LegalTypeCell,
            minSize: 180,
            maxSize: 180,
        },
        [ClientColumn.SUBSCRIPTION_START_DATE]: {
            accessorKey: "subscriptionStartDate",
            name: "Subscription date",
            header: "Subscription date",
            cell: SubscriptionStartDateCell,
            sort: {
                asc: WorkspaceSortValue.SUBSCRIPTION_START_DATE_ASC,
                desc: WorkspaceSortValue.SUBSCRIPTION_START_DATE_DESC,
            },
            minSize: 180,
            maxSize: 180,
        },
    },
    nesting: {
        enabled: true,
        expandColumn: ClientColumn.NAME,
        childrenKey: "entities",
    },
};
